import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Customer, Instance, Product, ProductPrice } from '../types';
import { api } from '../services/api';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import './InstancesListPage.css';

export const InstancesListPage = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [resettingInstance, setResettingInstance] = useState<Instance | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInstances();
    fetchProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.instance-actions')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      setError(null);
      if (user) {
        const data = await api.getInstancesByCustomer(user.customers);
        setCustomers(data);
      } else {
        setCustomers([]);
      }
    } catch (err) {
      setError('Failed to load instances. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await api.getProducts();
      const active = data.filter((p: Product) => p.active);
      setProducts(active);
      if (active.length > 0) setSelectedProductId(active[0].id);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const handleSendQwcFile = async (instance: Instance) => {
    setActionMessage(null);
    setActionError(null);
    setOpenMenuId(null);
    try {
      const result = await api.sendQwcFile(instance.instance);
      setActionMessage(result.message);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Request failed. Please try again.');
    }
  };

  const handleResetQBWCPassword = async (instance: Instance) => {
    setActionMessage(null);
    setActionError(null);
    setOpenMenuId(null);
    try {
      await api.forgotPassword(instance.instance);
      setResettingInstance(instance);
      setIsResetModalOpen(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Request failed. Please try again.');
    }
  };

  const formatProductLabel = (product: Product): string => {
    const price = product.prices.find((p: ProductPrice) => p.active);
    if (!price) return product.name;

    const amount = price.billingScheme === 'tiered' && price.tiers?.length
      ? price.tiers[0].unitAmount
      : price.unitAmount;

    const intervalLabel = price.recurring.interval === 'month' ? 'mo' : price.recurring.interval;
    const formattedPrice = `$${amount.toFixed(2)}/${intervalLabel}`;

    return `${product.name} — ${formattedPrice}`;
  };

  const handleAddInstance = () => {
    if (selectedProductId) {
      navigate(`/checkout?productId=${selectedProductId}`);
    }
  };

  const filteredCustomers = customers.map(customer => ({
    ...customer,
    instances: customer.instances.filter(instance =>
      instance.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instance.instance.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instance.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (instance.customerName || '').toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(customer => customer.instances.length > 0);

  if (loading) {
    return (
      <div className="instances-list-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading instances...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="instances-list-page">
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchInstances} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="instances-list-page">
      <div className="page-header">
        <h1>Your Instances</h1>
        <p className="subtitle">Manage your QuickBooks Desktop connections</p>
      </div>

      {actionMessage && (
        <div className="alert alert-success">{actionMessage}</div>
      )}
      {actionError && (
        <div className="alert alert-error">{actionError}</div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, instance, email, or organization..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredCustomers.length === 0 ? (
        <div className="no-results">
          <p>No instances found.</p>
        </div>
      ) : (
        <div className="customers-container">
          {filteredCustomers.map((customer) => (
            <div key={customer.id} className="customer-section">
              <h3 className="customer-name">{customer.name} ({customer.id})</h3>
              <div className="instances-grid">
                {customer.instances.map((instance) => {
                  const canSendQwc = !!(instance.qbdPath && instance.appUrl && instance.frequencyInMinutes);
                  return (
                    <div
                      key={instance.id}
                      className="instance-card"
                      onClick={() => navigate(`/instances/${instance.instance}`)}
                    >
                      <div className="instance-header">
                        <h4 className="instance-name">{instance.name}</h4>
                        <div className="instance-header-right">
                          <span className={`status-badge ${instance.validated ? 'validated' : 'not-validated'}`}>
                            {instance.validated ? 'Validated' : 'Not Validated'}
                          </span>
                          <div
                            className="instance-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="kebab-menu-button"
                              onClick={() => setOpenMenuId(openMenuId === instance.id ? null : instance.id)}
                              aria-label="Instance actions"
                              aria-haspopup="true"
                              aria-expanded={openMenuId === instance.id}
                            >
                              &#8942;
                            </button>
                            {openMenuId === instance.id && (
                              <div className="kebab-menu-dropdown" role="menu">
                                <button
                                  className="kebab-menu-item"
                                  role="menuitem"
                                  onClick={() => { navigate(`/instances/${instance.instance}/dashboard`); setOpenMenuId(null); }}
                                >
                                  Dashboard
                                </button>
                                <button
                                  className="kebab-menu-item"
                                  role="menuitem"
                                  onClick={() => { navigate(`/instances/${instance.instance}/monitoring`); setOpenMenuId(null); }}
                                >
                                  Monitoring
                                </button>
                                <button
                                  className="kebab-menu-item"
                                  role="menuitem"
                                  onClick={() => setOpenMenuId(null)}
                                >
                                  Test Connection
                                </button>
                                <button
                                  className="kebab-menu-item"
                                  role="menuitem"
                                  onClick={() => handleSendQwcFile(instance)}
                                  disabled={!canSendQwc}
                                >
                                  Send QWC File
                                </button>
                                <button
                                  className="kebab-menu-item"
                                  role="menuitem"
                                  onClick={() => handleResetQBWCPassword(instance)}
                                >
                                  Reset QBWC Password
                                </button>
                                <div className="kebab-menu-divider" />
                                <button
                                  className="kebab-menu-item danger"
                                  role="menuitem"
                                  onClick={() => setOpenMenuId(null)}
                                >
                                  Delete Instance
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="instance-details">
                        <div className="detail-column">
                          <div className="detail-row">
                            <span className="detail-label">Instance:</span>
                            <span className="detail-value">{instance.instance}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Instance Name:</span>
                            <span className="detail-value">{instance.name}</span>
                          </div>
                        </div>
                        <div className="detail-column">
                          <div className="detail-row">
                            <span className="detail-label">Organization:</span>
                            <span className="detail-value">{instance.customerName || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Sync Frequency:</span>
                            <span className="detail-value">{instance.frequencyInMinutes} minutes</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {products.length > 0 && (
        <div className="add-instance-section">
          <div className="add-instance-controls">
            <select
              className="product-select"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
            >
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {formatProductLabel(product)}
                </option>
              ))}
            </select>
            <button
              className="add-instance-button"
              onClick={handleAddInstance}
              disabled={!selectedProductId}
            >
              Add to Cart
            </button>
          </div>
          <a
            href="https://qbdconnect.com#pricing"
            className="compare-products-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Compare all products
          </a>
        </div>
      )}

      {resettingInstance && (
        <ResetPasswordModal
          isOpen={isResetModalOpen}
          username={resettingInstance.instance}
          onClose={() => { setIsResetModalOpen(false); setResettingInstance(null); }}
        />
      )}
    </div>
  );
};
